import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, RotateCcw, Eye, Plus, Trash2, GripVertical,
  ChevronDown, ChevronRight, Type, Users, BarChart3,
  Layout, MessageSquare, Footprints, Upload, Loader2, PanelRightOpen, PanelRightClose
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms/about';
const STORAGE_API = process.env.REACT_APP_BACKEND_URL + '/api/wms/storage';

const ICON_OPTIONS = ['Target', 'Shield', 'Zap', 'Globe', 'Package', 'Brain', 'Lock', 'Truck', 'BarChart3', 'Users', 'Heart', 'Star', 'Award', 'Briefcase', 'Code', 'Database'];
const COLOR_OPTIONS = ['blue', 'emerald', 'amber', 'violet', 'rose', 'cyan', 'indigo', 'gray', 'red', 'orange'];

// ── Drag and drop ───────────────────────────────────────────────
function useDragReorder(items, onReorder) {
  const dragIdx = useRef(null);
  const overIdx = useRef(null);
  const [dragActive, setDragActive] = useState(null);
  const [overActive, setOverActive] = useState(null);

  const onDragStart = useCallback((i) => (e) => {
    dragIdx.current = i;
    setDragActive(i);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  }, []);

  const onDragOver = useCallback((i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    overIdx.current = i;
    setOverActive(i);
  }, []);

  const onDrop = useCallback((i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from !== null && from !== i) {
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      onReorder(next);
    }
    dragIdx.current = null; overIdx.current = null;
    setDragActive(null); setOverActive(null);
  }, [items, onReorder]);

  const onDragEnd = useCallback(() => {
    dragIdx.current = null; overIdx.current = null;
    setDragActive(null); setOverActive(null);
  }, []);

  return { onDragStart, onDragOver, onDrop, onDragEnd, dragActive, overActive };
}

function DragHandle({ idx, drag }) {
  return (
    <div draggable onDragStart={drag.onDragStart(idx)} onDragEnd={drag.onDragEnd}
      className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded transition-colors"
      data-testid={`drag-handle-${idx}`}>
      <GripVertical className="w-4 h-4 text-gray-300" />
    </div>
  );
}

function DragRow({ idx, drag, children }) {
  const isDrag = drag.dragActive === idx;
  const isOver = drag.overActive === idx && drag.dragActive !== idx;
  return (
    <div onDragOver={drag.onDragOver(idx)} onDrop={drag.onDrop(idx)}
      className={`transition-all duration-150 ${isDrag ? 'opacity-40 scale-[0.98]' : ''} ${isOver ? 'ring-2 ring-blue-300 ring-offset-1 rounded-lg' : ''}`}>
      {children}
    </div>
  );
}

// ── UI primitives ───────────────────────────────────────────────
function SectionWrapper({ id, label, icon: Icon, color, open, onToggle, children }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600', emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600', violet: 'bg-violet-50 text-violet-600',
    rose: 'bg-rose-50 text-rose-600', cyan: 'bg-cyan-50 text-cyan-600',
    indigo: 'bg-indigo-50 text-indigo-600', gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={`editor-section-${id}`}>
      <button onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors[color] || colors.gray}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold text-xs">{label}</span>
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
      </button>
      {open && <div className="border-t px-4 py-4 space-y-3">{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, multiline, placeholder, small }) {
  const cls = `w-full border rounded-lg px-2.5 ${small ? 'py-1 text-xs' : 'py-1.5 text-xs'} focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-all`;
  return (
    <div>
      {label && <label className="text-[10px] font-medium text-gray-500 block mb-0.5">{label}</label>}
      {multiline ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2} className={cls} placeholder={placeholder} />
      ) : (
        <input value={value || ''} onChange={e => onChange(e.target.value)} className={cls} placeholder={placeholder} />
      )}
    </div>
  );
}

function ImageUpload({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef();
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Only images'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    setUploading(true);
    const form = new FormData(); form.append('file', file);
    try {
      const r = await axios.post(`${STORAGE_API}/upload`, form);
      onChange(process.env.REACT_APP_BACKEND_URL + r.data.url);
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  return (
    <div>
      {label && <label className="text-[10px] font-medium text-gray-500 block mb-0.5">{label}</label>}
      <div className="flex gap-1.5">
        <input value={value || ''} onChange={e => onChange(e.target.value)}
          className="flex-1 border rounded-lg px-2.5 py-1.5 text-xs focus:ring-2 focus:ring-blue-200 outline-none"
          placeholder="Paste URL or upload" />
        <input type="file" ref={fileRef} accept="image/*" onChange={handleUpload} className="hidden" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn"
          className="flex items-center gap-1 text-[10px] border px-2 py-1.5 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 text-gray-600 shrink-0">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? '...' : 'Upload'}
        </button>
      </div>
      {value && <img src={value} alt="" className="w-full h-20 object-cover rounded-lg border mt-1.5" onError={e => { e.target.style.display = 'none'; }} />}
    </div>
  );
}

// ── Live Preview HTML builder ───────────────────────────────────
function buildPreviewHtml(data) {
  if (!data) return '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;color:#999;font-family:Arial">Loading...</body></html>';

  const { company_name, hero, stats, story, values, capabilities, team, cta, footer } = data;

  const iconSvgs = {
    Target: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/><circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" fill="none"/><circle cx="12" cy="12" r="2" fill="currentColor"/>',
    Shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="2" fill="none"/>',
    Zap: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10" stroke="currentColor" stroke-width="2" fill="none"/>',
    Globe: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/><line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" stroke-width="2" fill="none"/>',
  };

  const colorMap = {
    blue: { text: '#2563eb', bg: '#eff6ff' }, emerald: { text: '#059669', bg: '#ecfdf5' },
    amber: { text: '#d97706', bg: '#fffbeb' }, violet: { text: '#7c3aed', bg: '#f5f3ff' },
    rose: { text: '#e11d48', bg: '#fff1f2' }, cyan: { text: '#0891b2', bg: '#ecfeff' },
    indigo: { text: '#4f46e5', bg: '#eef2ff' }, gray: { text: '#4b5563', bg: '#f9fafb' },
  };

  let html = `<html><head><style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:Arial,sans-serif; color:#333; line-height:1.6; background:#fff; }
    .container { max-width:700px; margin:0 auto; padding:0 20px; }
  </style></head><body>`;

  // Hero
  html += `<div style="padding:40px 20px"><div class="container">
    ${hero?.badge_text ? `<div style="display:inline-block;background:#eff6ff;color:#2563eb;font-size:10px;font-weight:600;padding:4px 10px;border-radius:20px;margin-bottom:16px">${hero.badge_text}</div>` : ''}
    <h1 style="font-size:32px;font-weight:800;color:#111;line-height:1.1">${hero?.heading_line1 || ''}<br><span style="color:#1e3a5f">${hero?.heading_line2 || ''}</span></h1>
    <p style="margin-top:12px;font-size:13px;color:#6b7280;max-width:500px">${hero?.description || ''}</p>
    ${hero?.image_url ? `<img src="${hero.image_url}" style="width:100%;border-radius:12px;margin-top:20px;aspect-ratio:4/3;object-fit:cover" />` : ''}
  </div></div>`;

  // Stats
  if (stats?.length > 0) {
    html += `<div style="background:#1e3a5f;padding:24px 20px"><div class="container" style="display:grid;grid-template-columns:repeat(${Math.min(stats.length, 4)},1fr);gap:16px;text-align:center">`;
    stats.forEach(s => {
      html += `<div><div style="font-size:22px;font-weight:700;color:#fff">${s.value}</div><div style="font-size:10px;color:#93c5fd;margin-top:2px">${s.label}</div></div>`;
    });
    html += `</div></div>`;
  }

  // Story
  if (story?.paragraphs?.length > 0) {
    html += `<div style="padding:40px 20px"><div class="container">
      <div style="font-size:9px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:2px;margin-bottom:8px">Our Story</div>
      <h2 style="font-size:20px;font-weight:700;color:#111">${story.heading || ''}</h2>
      <div style="margin-top:16px">`;
    story.paragraphs.forEach((p, i) => {
      html += `<p style="font-size:12px;color:${i === story.paragraphs.length - 1 ? '#374151;font-weight:500' : '#6b7280'};margin-bottom:12px">${p}</p>`;
    });
    html += `</div></div></div>`;
  }

  // Values
  if (values?.length > 0) {
    html += `<div style="padding:40px 20px;background:#f9fafb"><div class="container">
      <div style="text-align:center;margin-bottom:24px"><div style="font-size:9px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:2px;margin-bottom:6px">Core Values</div><h2 style="font-size:20px;font-weight:700;color:#111">What we stand for</h2></div>
      <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px">`;
    values.forEach(v => {
      const cm = colorMap[v.color] || colorMap.blue;
      html += `<div style="background:#fff;border-radius:12px;padding:20px;border:1px solid #f3f4f6">
        <div style="width:36px;height:36px;background:${cm.bg};border-radius:8px;display:flex;align-items:center;justify-content:center;margin-bottom:12px">
          <svg width="18" height="18" viewBox="0 0 24 24" style="color:${cm.text}">${iconSvgs[v.icon] || iconSvgs.Target}</svg>
        </div>
        <div style="font-size:14px;font-weight:700;color:#111;margin-bottom:4px">${v.title}</div>
        <div style="font-size:11px;color:#6b7280;line-height:1.5">${v.desc}</div>
      </div>`;
    });
    html += `</div></div></div>`;
  }

  // Capabilities
  if (capabilities?.length > 0) {
    html += `<div style="padding:40px 20px"><div class="container">
      <div style="text-align:center;margin-bottom:24px"><div style="font-size:9px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:2px;margin-bottom:6px">Platform Capabilities</div><h2 style="font-size:20px;font-weight:700;color:#111">Enterprise-grade from day one</h2></div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px">`;
    capabilities.forEach(c => {
      html += `<div><div style="font-size:12px;font-weight:600;color:#111;margin-bottom:2px">${c.title}</div><div style="font-size:10px;color:#6b7280;line-height:1.5">${c.desc}</div></div>`;
    });
    html += `</div></div></div>`;
  }

  // Team
  if (team?.members?.length > 0) {
    html += `<div style="padding:40px 20px;background:#f9fafb"><div class="container">
      <div style="font-size:9px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:2px;margin-bottom:8px">Leadership</div>
      <h2 style="font-size:20px;font-weight:700;color:#111">${team.heading || ''}</h2>
      ${team.description ? `<p style="margin-top:8px;font-size:12px;color:#6b7280">${team.description}</p>` : ''}
      <div style="display:grid;grid-template-columns:repeat(${Math.min(team.members.length, 4)},1fr);gap:12px;margin-top:20px">`;
    team.members.forEach(m => {
      const initials = m.name ? m.name.split(' ').map(n => n[0]).join('') : '?';
      html += `<div style="background:#fff;border-radius:12px;padding:16px;border:1px solid #f3f4f6">
        ${m.photo_url
          ? `<img src="${m.photo_url}" style="width:40px;height:40px;border-radius:50%;object-fit:cover;margin-bottom:10px" />`
          : `<div style="width:40px;height:40px;border-radius:50%;background:#1e3a5f;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;margin-bottom:10px">${initials}</div>`
        }
        <div style="font-weight:700;color:#111;font-size:12px">${m.name}</div>
        <div style="font-size:9px;color:#1e3a5f;font-weight:500;margin-top:1px">${m.role}</div>
        <div style="font-size:9px;color:#6b7280;margin-top:6px;line-height:1.4">${m.bio}</div>
      </div>`;
    });
    html += `</div></div></div>`;
  }

  // CTA
  html += `<div style="padding:40px 20px;text-align:center"><div class="container">
    <h2 style="font-size:20px;font-weight:700;color:#111">${cta?.heading || ''}</h2>
    <p style="margin-top:8px;font-size:12px;color:#6b7280;max-width:400px;margin-left:auto;margin-right:auto">${cta?.description || ''}</p>
    <div style="margin-top:16px"><span style="display:inline-block;background:#1e3a5f;color:#fff;padding:10px 24px;border-radius:10px;font-size:12px;font-weight:500">${cta?.button_text || 'Get Started'}</span></div>
  </div></div>`;

  // Footer
  html += `<div style="border-top:1px solid #f3f4f6;padding:20px;text-align:center">
    <span style="font-size:10px;font-weight:600;color:#111">${company_name || ''} WMS</span>
    <span style="font-size:9px;color:#9ca3af;margin-left:8px">${footer?.company_line || ''} &middot; ${new Date().getFullYear()}</span>
  </div>`;

  html += `</body></html>`;
  return html;
}

// ── Main Editor ─────────────────────────────────────────────────
const SECTION_META = [
  { key: 'hero', label: 'Hero', icon: Layout, color: 'blue' },
  { key: 'stats', label: 'Stats', icon: BarChart3, color: 'emerald' },
  { key: 'story', label: 'Story', icon: Type, color: 'amber' },
  { key: 'values', label: 'Values', icon: MessageSquare, color: 'violet' },
  { key: 'capabilities', label: 'Capabilities', icon: BarChart3, color: 'cyan' },
  { key: 'team', label: 'Team', icon: Users, color: 'rose' },
  { key: 'cta', label: 'CTA', icon: MessageSquare, color: 'indigo' },
  { key: 'footer', label: 'Footer', icon: Footprints, color: 'gray' },
];

export default function AboutEditor() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openSections, setOpenSections] = useState({ hero: true });
  const [showPreview, setShowPreview] = useState(true);
  const previewRef = useRef(null);

  useEffect(() => { loadContent(); }, []);

  // Update preview iframe whenever content changes
  useEffect(() => {
    if (previewRef.current && content) {
      const doc = previewRef.current.contentDocument || previewRef.current.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(buildPreviewHtml(content));
        doc.close();
      }
    }
  }, [content, showPreview]);

  const loadContent = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/content`); setContent(r.data); } catch {}
    setLoading(false);
  };

  const saveContent = async () => {
    setSaving(true); setSaved(false);
    try { await axios.put(`${API}/admin/content`, content); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    catch { alert('Failed to save'); }
    setSaving(false);
  };

  const resetContent = async () => {
    if (!window.confirm('Reset all About page content to defaults? This cannot be undone.')) return;
    try { await axios.post(`${API}/admin/reset`); await loadContent(); } catch { alert('Failed to reset'); }
  };

  const toggle = (key) => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  const statsDrag = useDragReorder(content?.stats || [], (next) => set('stats', next));
  const valuesDrag = useDragReorder(content?.values || [], (next) => set('values', next));
  const capsDrag = useDragReorder(content?.capabilities || [], (next) => set('capabilities', next));
  const membersDrag = useDragReorder(content?.team?.members || [], (next) => set('team.members', next));

  const set = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        ref = ref[k];
      }
      ref[isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1])] = value;
      return next;
    });
  };

  if (loading || !content) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400 text-sm">Loading editor...</div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="about-editor">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm shrink-0">
        <div className="px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/wms')} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="editor-back">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div>
              <h1 className="text-xs font-bold text-gray-900">About Page Editor</h1>
              <p className="text-[9px] text-gray-400">/about-us</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {saved && <span className="text-[10px] text-emerald-600 font-medium" data-testid="save-success">Saved!</span>}
            <button onClick={() => setShowPreview(p => !p)}
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 px-2 py-1.5 border rounded-lg hover:bg-gray-50"
              data-testid="toggle-preview">
              {showPreview ? <PanelRightClose className="w-3 h-3" /> : <PanelRightOpen className="w-3 h-3" />}
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <a href="/about-us" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 px-2 py-1.5 border rounded-lg hover:bg-gray-50"
              data-testid="editor-preview-link">
              <Eye className="w-3 h-3" />Full Page
            </a>
            <button onClick={resetContent}
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-red-600 px-2 py-1.5 border rounded-lg hover:bg-red-50"
              data-testid="editor-reset">
              <RotateCcw className="w-3 h-3" />Reset
            </button>
            <button onClick={saveContent} disabled={saving}
              className="flex items-center gap-1 text-[10px] bg-[#1e3a5f] text-white px-4 py-1.5 rounded-lg hover:bg-[#162d4a] disabled:bg-gray-300 font-medium"
              data-testid="editor-save">
              <Save className="w-3 h-3" />{saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Split layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Editor panel */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} overflow-y-auto transition-all duration-300`}
          data-testid="editor-panel">
          <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
            {/* Company Name */}
            <div className="bg-white rounded-xl border shadow-sm px-4 py-3">
              <Field label="Company Name" value={content.company_name}
                onChange={v => set('company_name', v)} placeholder="Your Company Name" />
            </div>

            {/* Hero */}
            <SectionWrapper id="hero" label="Hero Section" icon={Layout} color="blue"
              open={openSections.hero} onToggle={() => toggle('hero')}>
              <Field label="Badge Text (optional)" value={content.hero?.badge_text}
                onChange={v => set('hero.badge_text', v)} placeholder="Leave empty to hide" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Heading Line 1" value={content.hero?.heading_line1}
                  onChange={v => set('hero.heading_line1', v)} />
                <Field label="Heading Line 2 (accent)" value={content.hero?.heading_line2}
                  onChange={v => set('hero.heading_line2', v)} />
              </div>
              <Field label="Description" value={content.hero?.description}
                onChange={v => set('hero.description', v)} multiline />
              <div className="grid grid-cols-2 gap-3">
                <Field label="CTA Text" value={content.hero?.cta_text}
                  onChange={v => set('hero.cta_text', v)} />
                <Field label="CTA Link" value={content.hero?.cta_link}
                  onChange={v => set('hero.cta_link', v)} />
              </div>
              <ImageUpload label="Hero Image" value={content.hero?.image_url}
                onChange={v => set('hero.image_url', v)} />
            </SectionWrapper>

            {/* Stats */}
            <SectionWrapper id="stats" label="Stats Bar" icon={BarChart3} color="emerald"
              open={openSections.stats} onToggle={() => toggle('stats')}>
              <div className="space-y-2">
                {(content.stats || []).map((s, i) => (
                  <DragRow key={i} idx={i} drag={statsDrag}>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5">
                      <DragHandle idx={i} drag={statsDrag} />
                      <div className="grid grid-cols-2 gap-2 flex-1">
                        <Field value={s.value} onChange={v => set(`stats.${i}.value`, v)} placeholder="50M+" small />
                        <Field value={s.label} onChange={v => set(`stats.${i}.label`, v)} placeholder="Label" small />
                      </div>
                      <button onClick={() => { const n = [...content.stats]; n.splice(i,1); set('stats',n); }}
                        className="p-1 hover:bg-red-50 rounded" data-testid={`remove-stat-${i}`}>
                        <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  </DragRow>
                ))}
                <button onClick={() => set('stats', [...(content.stats || []), {value:'',label:''}])}
                  className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="add-stat">
                  <Plus className="w-3 h-3" />Add Stat
                </button>
              </div>
            </SectionWrapper>

            {/* Story */}
            <SectionWrapper id="story" label="Our Story" icon={Type} color="amber"
              open={openSections.story} onToggle={() => toggle('story')}>
              <Field label="Heading" value={content.story?.heading}
                onChange={v => set('story.heading', v)} />
              <div className="space-y-2">
                <label className="text-[10px] font-medium text-gray-500">Paragraphs</label>
                {(content.story?.paragraphs || []).map((p, i) => (
                  <div key={i} className="flex gap-1.5">
                    <textarea value={p} onChange={e => set(`story.paragraphs.${i}`, e.target.value)}
                      rows={2} className="flex-1 border rounded-lg px-2.5 py-1.5 text-xs focus:ring-2 focus:ring-blue-200 outline-none" />
                    <button onClick={() => { const n = [...content.story.paragraphs]; n.splice(i,1); set('story.paragraphs',n); }}
                      className="p-1 self-start hover:bg-red-50 rounded" data-testid={`remove-paragraph-${i}`}>
                      <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
                <button onClick={() => set('story.paragraphs', [...(content.story?.paragraphs || []), ''])}
                  className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="add-paragraph">
                  <Plus className="w-3 h-3" />Add Paragraph
                </button>
              </div>
            </SectionWrapper>

            {/* Values */}
            <SectionWrapper id="values" label="Core Values" icon={MessageSquare} color="violet"
              open={openSections.values} onToggle={() => toggle('values')}>
              <div className="space-y-3">
                {(content.values || []).map((v, i) => (
                  <DragRow key={i} idx={i} drag={valuesDrag}>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <DragHandle idx={i} drag={valuesDrag} />
                          <span className="text-[10px] font-semibold text-gray-500">Value {i+1}</span>
                        </div>
                        <button onClick={() => { const n = [...content.values]; n.splice(i,1); set('values',n); }}
                          className="p-1 hover:bg-red-50 rounded" data-testid={`remove-value-${i}`}>
                          <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Field label="Title" value={v.title} onChange={val => set(`values.${i}.title`, val)} small />
                        <div><label className="text-[10px] font-medium text-gray-500 block mb-0.5">Icon</label>
                          <select value={v.icon||''} onChange={e => set(`values.${i}.icon`, e.target.value)}
                            className="w-full border rounded-lg px-2 py-1 text-[10px] bg-white">
                            {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                          </select></div>
                        <div><label className="text-[10px] font-medium text-gray-500 block mb-0.5">Color</label>
                          <select value={v.color||'blue'} onChange={e => set(`values.${i}.color`, e.target.value)}
                            className="w-full border rounded-lg px-2 py-1 text-[10px] bg-white">
                            {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select></div>
                      </div>
                      <Field label="Description" value={v.desc} onChange={val => set(`values.${i}.desc`, val)} multiline small />
                    </div>
                  </DragRow>
                ))}
                <button onClick={() => set('values', [...(content.values||[]), {icon:'Star',title:'',desc:'',color:'blue'}])}
                  className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="add-value">
                  <Plus className="w-3 h-3" />Add Value
                </button>
              </div>
            </SectionWrapper>

            {/* Capabilities */}
            <SectionWrapper id="capabilities" label="Capabilities" icon={BarChart3} color="cyan"
              open={openSections.capabilities} onToggle={() => toggle('capabilities')}>
              <div className="space-y-2">
                {(content.capabilities || []).map((c, i) => (
                  <DragRow key={i} idx={i} drag={capsDrag}>
                    <div className="flex gap-2 bg-gray-50 rounded-lg p-2.5 items-start">
                      <DragHandle idx={i} drag={capsDrag} />
                      <select value={c.icon||''} onChange={e => set(`capabilities.${i}.icon`, e.target.value)}
                        className="border rounded px-1 py-0.5 text-[10px] bg-white shrink-0 mt-0.5">
                        {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                      <div className="flex-1 space-y-1.5">
                        <Field value={c.title} onChange={v => set(`capabilities.${i}.title`, v)} placeholder="Title" small />
                        <Field value={c.desc} onChange={v => set(`capabilities.${i}.desc`, v)} placeholder="Description" small />
                      </div>
                      <button onClick={() => { const n = [...content.capabilities]; n.splice(i,1); set('capabilities',n); }}
                        className="p-1 hover:bg-red-50 rounded shrink-0" data-testid={`remove-cap-${i}`}>
                        <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  </DragRow>
                ))}
                <button onClick={() => set('capabilities', [...(content.capabilities||[]), {icon:'Star',title:'',desc:''}])}
                  className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="add-capability">
                  <Plus className="w-3 h-3" />Add Capability
                </button>
              </div>
            </SectionWrapper>

            {/* Team */}
            <SectionWrapper id="team" label="Leadership Team" icon={Users} color="rose"
              open={openSections.team} onToggle={() => toggle('team')}>
              <Field label="Heading" value={content.team?.heading}
                onChange={v => set('team.heading', v)} />
              <Field label="Description" value={content.team?.description}
                onChange={v => set('team.description', v)} multiline />
              <ImageUpload label="Team Photo" value={content.team?.image_url}
                onChange={v => set('team.image_url', v)} />
              <div className="border-t pt-3 space-y-3">
                <label className="text-[10px] font-medium text-gray-500">Members</label>
                {(content.team?.members || []).map((m, i) => (
                  <DragRow key={i} idx={i} drag={membersDrag}>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid={`team-member-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <DragHandle idx={i} drag={membersDrag} />
                          <div className="w-6 h-6 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white text-[9px] font-bold">
                            {m.name ? m.name.split(' ').map(n => n[0]).join('') : '?'}
                          </div>
                          <span className="text-[10px] font-semibold text-gray-700">{m.name || 'New'}</span>
                        </div>
                        <button onClick={() => { const n = [...content.team.members]; n.splice(i,1); set('team.members',n); }}
                          className="p-1 hover:bg-red-50 rounded" data-testid={`remove-member-${i}`}>
                          <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Name" value={m.name} onChange={v => set(`team.members.${i}.name`, v)} small />
                        <Field label="Role" value={m.role} onChange={v => set(`team.members.${i}.role`, v)} small />
                      </div>
                      <Field label="Bio" value={m.bio} onChange={v => set(`team.members.${i}.bio`, v)} multiline small />
                      <ImageUpload label="Photo" value={m.photo_url}
                        onChange={v => set(`team.members.${i}.photo_url`, v)} />
                    </div>
                  </DragRow>
                ))}
                <button onClick={() => set('team.members', [...(content.team?.members||[]), {name:'',role:'',bio:'',photo_url:''}])}
                  className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="add-member">
                  <Plus className="w-3 h-3" />Add Member
                </button>
              </div>
            </SectionWrapper>

            {/* CTA */}
            <SectionWrapper id="cta" label="Call to Action" icon={MessageSquare} color="indigo"
              open={openSections.cta} onToggle={() => toggle('cta')}>
              <Field label="Heading" value={content.cta?.heading}
                onChange={v => set('cta.heading', v)} />
              <Field label="Description" value={content.cta?.description}
                onChange={v => set('cta.description', v)} multiline />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Button Text" value={content.cta?.button_text}
                  onChange={v => set('cta.button_text', v)} />
                <Field label="Button Link" value={content.cta?.button_link}
                  onChange={v => set('cta.button_link', v)} />
              </div>
            </SectionWrapper>

            {/* Footer */}
            <SectionWrapper id="footer" label="Footer" icon={Footprints} color="gray"
              open={openSections.footer} onToggle={() => toggle('footer')}>
              <Field label="Company Line" value={content.footer?.company_line}
                onChange={v => set('footer.company_line', v)} />
            </SectionWrapper>
          </div>
        </div>

        {/* Live preview panel */}
        {showPreview && (
          <div className="w-1/2 border-l bg-white flex flex-col" data-testid="preview-panel">
            <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between shrink-0">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Live Preview</span>
              <span className="text-[9px] text-gray-400">Changes appear as you type</span>
            </div>
            <iframe ref={previewRef} title="About Preview"
              className="flex-1 w-full border-0"
              data-testid="live-preview-iframe"
              sandbox="allow-same-origin" />
          </div>
        )}
      </div>
    </div>
  );
}
